import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import {
  AirspaceStatus,
  ReferenceType,
  REFERENCE_TYPE_LABELS,
  fetchUASFM,
  skyvectorUrl,
  aloftLaancUrl,
  faaNotamSearchUrl,
  FAA_DRONEZONE_URL,
  FAA_PART107_WAIVER_URL,
  FAA_TFR_URL,
  userInitials,
  UASFMResult,
} from '../utils/airspace-helpers';
import { formatEventDate } from '../utils/date-utils';
import AirspaceStatusPill from '../components/AirspaceStatusPill';
import AirspaceMap from './AirspaceMap';
import AirspaceReferenceRow from './AirspaceReferenceRow';
import NearbyHistoricalEvents from './NearbyHistoricalEvents';
import MessageThread from '../components/MessageThread/MessageThread';
import { buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import Select from '../components/ui/Select';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { useSiteMapShare } from '../Events/useSiteMapShare';

interface FullData {
  event: {
    id: string;
    name: string;
    event_date: string;
    rain_date: string | null;
    location: string | null;
    launch_latitude: number | null;
    launch_longitude: number | null;
    audience_latitude: number | null;
    audience_longitude: number | null;
    show_height_ft: number | null;
    surface_type: string | null;
    site_map_url: string | null;
    controlling_facility: string | null;
    airspace_status: AirspaceStatus;
    airspace_needed: boolean | null;
    airspace_coords_verified_at: string | null;
    airspace_coords_verified_by: string | null;
    airspace_decided_at: string | null;
    airspace_decided_by: string | null;
    airspace_completed_at: string | null;
    airspace_completed_by: string | null;
    airspace_no_location_ack_at: string | null;
    airspace_no_location_ack_by: string | null;
    no_location_ack_firstname: string | null;
    no_location_ack_lastname: string | null;
    airspace_drive_folder_id: string | null;
    airspace_invalidation_count: number;
    verified_by_firstname: string | null;
    verified_by_lastname: string | null;
    decided_by_firstname: string | null;
    decided_by_lastname: string | null;
    completed_by_firstname: string | null;
    completed_by_lastname: string | null;
  };
  activeRefs: Array<any>;
  archivedRefs: Array<any>;
  audit: Array<any>;
}

interface Props {
  eventId: string;
  onClose: () => void;
  /** Called after every successful mutation (decide, verify, ref change, etc.)
   *  so the parent can refresh the queue list. */
  onMutated?: () => void;
  /** Switch the active event in-place (e.g. clicking a nearby historical event).
   *  Provided by PilotsPage so navigation doesn't hard-reload the page. */
  onSelectEvent?: (id: string) => void;
}

const REF_TYPES: ReferenceType[] = ['airspace_authorization', 'altitude_waiver', 'tfr_waiver', 'rain_date_authorization', 'reapply_higher'];

// Section header — mirrors the FleetDashboard pattern (small colored bar + uppercase label).
const SectionHeader: React.FC<{ label: string; color?: string; trailing?: React.ReactNode }> = ({ label, color = 'bg-blue-500/60', trailing }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2.5">
      <div className={`w-1 h-3.5 rounded-full ${color}`} />
      <h3 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold">{label}</h3>
    </div>
    {trailing}
  </div>
);

// Sign-off badge — initials chip + relative date. Mirrors the per-reference
// "MC · 2026-05-03" display used inside AirspaceReferenceRow so all sign-offs
// across the workflow read the same way.
const SignOff: React.FC<{ firstname?: string | null; lastname?: string | null; at?: string | null; verb?: string }> = ({ firstname, lastname, at, verb = 'Signed off' }) => {
  if (!at) return null;
  const initials = userInitials(firstname, lastname);
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-on-surface-variant">
      <Tooltip label={`${verb} by ${firstname || ''} ${lastname || ''}`.trim()}>
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/20 text-primary-ink font-semibold text-[10px] ring-1 ring-inset ring-primary/30">
          {initials}
        </span>
      </Tooltip>
      <span className="text-on-surface-variant">{verb} · {formatEventDate(at)}</span>
    </span>
  );
};

// External-link button used in the Reference Links grid. Two-line layout:
// label + tiny hint underneath. The `emphasis` variant tints the border to
// flag the most important destinations (LAANC submission, DroneZone).
const ExternalButton: React.FC<{ href: string; label: string; hint?: string; emphasis?: boolean }> = ({ href, label, hint, emphasis }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className={`group flex items-start gap-2 px-3 py-2 rounded-md border bg-gray-900/40 hover:bg-gray-900/80 transition-colors ${
      emphasis
        ? 'border-primary/40 hover:border-primary/70'
        : 'border-gray-700/50 hover:border-outline-variant'
    }`}
  >
    <div className="flex-1 min-w-0">
      <div className={`text-xs font-medium truncate ${emphasis ? 'text-primary' : 'text-on-surface group-hover:text-white'}`}>
        {label}
      </div>
      {hint && <div className="text-[10px] text-on-surface-variant mt-0.5 truncate">{hint}</div>}
    </div>
    <span className={`text-xs ${emphasis ? 'text-primary' : 'text-on-surface-variant group-hover:text-on-surface'}`}>↗</span>
  </a>
);

const AirspaceEventDetail: React.FC<Props> = ({ eventId, onMutated, onSelectEvent }) => {
  const api = useApi();
  const toast = useToast();
  const [data, setData] = useState<FullData | null>(null);
  const [loading, setLoading] = useState(true);
  const [uasfm, setUasfm] = useState<UASFMResult | null>(null);
  const [uasfmError, setUasfmError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [acking, setAcking] = useState(false);

  const refresh = useCallback(async (opts: { afterMutation?: boolean } = {}) => {
    try {
      const d = await api.get(`/airspace/events/${eventId}`, { silent: true });
      setData(d);
      setLoading(false);
      // Notify parent so the queue list re-fetches and shows updated status —
      // only after real mutations, not on initial load / event-switch.
      if (opts.afterMutation) onMutated?.();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load airspace');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  // UASFM lookup once we have coords. NOTAMs are intentionally not fetched
  // here — see the "FAA NOTAM Search" reference link for manual verification.
  // `cancelled` flag drops stale responses so rapid event-switching doesn't
  // commit event-A data after event-B has been selected.
  useEffect(() => {
    if (!data?.event?.launch_latitude || !data?.event?.launch_longitude) return;
    const lat = data.event.launch_latitude;
    const lng = data.event.launch_longitude;
    let cancelled = false;
    setLookupLoading(true);
    setUasfmError(null);
    fetchUASFM(lat, lng)
      .then(u => { if (!cancelled) { setUasfm(u); setUasfmError(null); } })
      .catch(err => { if (!cancelled) { setUasfm(null); setUasfmError(err?.message || 'UASFM lookup failed'); } })
      .finally(() => { if (!cancelled) setLookupLoading(false); });
    return () => { cancelled = true; };
  }, [data?.event?.launch_latitude, data?.event?.launch_longitude]);

  const lookupSnapshot = () => ({
    uasfm: uasfm ? {
      max_ceiling_ft: uasfm.max_ceiling_ft,
      controlling_facility: uasfm.controlling_facility,
      facility_name: uasfm.facility_name,
      airspace_class: uasfm.airspace_class,
    } : null,
    fetched_at: new Date().toISOString(),
  });

  const handleVerifyCoords = async () => {
    try {
      await api.post(`/airspace/events/${eventId}/verify`, { body: { lookup_snapshot: lookupSnapshot() } });
      toast.success('Coordinates verified');
      refresh({ afterMutation: true });
    } catch (e: any) { toast.error(e?.message || 'Verify failed'); }
  };

  const handleAcknowledgeNoLocation = async () => {
    setAcking(true);
    try {
      await api.post(`/airspace/events/${eventId}/acknowledge-no-location`, { body: {} });
      toast.success('Acknowledged no location');
      refresh({ afterMutation: true });
    } catch (e: any) {
      toast.error(e?.message || 'Could not acknowledge');
    } finally {
      setAcking(false);
    }
  };

  const handleUndoNoLocation = async () => {
    setAcking(true);
    try {
      await api.post(`/airspace/events/${eventId}/acknowledge-no-location/undo`, { body: {} });
      toast.success('Back in the queue');
      refresh({ afterMutation: true });
    } catch (e: any) {
      toast.error(e?.message || 'Could not put it back');
    } finally {
      setAcking(false);
    }
  };

  const handleDecide = async (needed: boolean) => {
    try {
      await api.post(`/airspace/events/${eventId}/decide`, { body: { needed, lookup_snapshot: lookupSnapshot() } });
      toast.success(needed ? 'Authorization needed — references seeded' : 'Marked complete (no auth needed)');
      refresh({ afterMutation: true });
    } catch (e: any) { toast.error(e?.message || 'Decision failed'); }
  };

  const handleAddReference = async (type: ReferenceType) => {
    setAdding(true);
    try {
      await api.post(`/airspace/events/${eventId}/references`, { body: { type } });
      refresh({ afterMutation: true });
    } catch (e: any) { toast.error(e?.message || 'Add failed'); }
    finally { setAdding(false); }
  };

  // Reaffirm an archived reference — pilot reviewed the existing FAA approval
  // and is declaring it still valid for the (slightly changed) event. Backend
  // un-archives the row and restores the event-level sign-offs. See
  // docs/features/airspace-reaffirm.md.
  const [reaffirmingId, setReaffirmingId] = useState<string | null>(null);
  const handleReaffirm = async (refId: string) => {
    setReaffirmingId(refId);
    try {
      await api.post(`/airspace/references/${refId}/reaffirm`, { body: {} });
      toast.success('Reaffirmed — existing authorization still valid');
      refresh({ afterMutation: true });
    } catch (e: any) {
      toast.error(e?.message || 'Reaffirm failed');
    } finally {
      setReaffirmingId(null);
    }
  };

  // Wrapper passed to AirspaceReferenceRow so its mutations also bubble up.
  const refreshAfterMutation = useCallback(() => refresh({ afterMutation: true }), [refresh]);

  // Hook is called unconditionally (rules-of-hooks); it no-ops when the
  // event isn't loaded yet, since `parseShareUrl(undefined)` returns null.
  const siteMapShare = useSiteMapShare(data?.event?.site_map_url);

  if (loading) {
    return (
      <SkeletonGroup label="Loading the event" className="space-y-3 p-6">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-24 w-full" />
      </SkeletonGroup>
    );
  }
  if (!data) return <div className="p-6"><Alert>Failed to load.</Alert></div>;

  const ev = data.event;
  const hasCoords = ev.launch_latitude != null && ev.launch_longitude != null;
  const coordsVerified = !!ev.airspace_coords_verified_at;
  const decisionMade = ev.airspace_needed !== null && ev.airspace_needed !== undefined;
  const noLocationAcked = !!ev.airspace_no_location_ack_at;
  // Only a fresh review can be closed this way. A show that already had a
  // decision, then lost its pin, stays in the queue.
  const canAcknowledgeNoLocation = !hasCoords && !noLocationAcked
    && ev.airspace_status === 'needs_review' && !coordsVerified && !decisionMade;

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header — sticky to the top of the scroll container so event identity
          and status pill stay visible while the pilot scrolls through workflow.
          z-[1100] sits above leaflet's pane stack (capped at 7 in index.css) so the map can't
          poke through the header during scroll. */}
      <div className="sticky top-0 z-[1100] px-5 py-4 border-b border-outline-variant bg-surface-container flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-white truncate">{ev.name}</h2>
          <div className="text-xs text-on-surface-variant mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{formatEventDate(ev.event_date)}</span>
            {ev.rain_date && <span className="text-amber-400/90">rain {formatEventDate(ev.rain_date)}</span>}
            {ev.location && <span className="truncate">{ev.location}</span>}
            {ev.show_height_ft != null && <span>{ev.show_height_ft} ft</span>}
            {ev.surface_type && <span className="capitalize">{ev.surface_type}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <AirspaceStatusPill status={ev.airspace_status} size="full" />
          {ev.airspace_status === 'complete' && ev.airspace_completed_at && (
            <SignOff
              firstname={ev.completed_by_firstname}
              lastname={ev.completed_by_lastname}
              at={ev.airspace_completed_at}
              verb="Completed"
            />
          )}
          {ev.airspace_invalidation_count > 0 && (
            <span className="text-[10px] text-amber-400/80">attempt #{ev.airspace_invalidation_count + 1}</span>
          )}
        </div>
      </div>

      {/* Map — full width, taller. Spans both workflow + nearby-history columns. */}
      <div className={`${hasCoords ? 'h-[500px]' : 'h-28'} border-b border-gray-700/40 flex-shrink-0`}>
        {hasCoords ? (
          <AirspaceMap
            launchLat={ev.launch_latitude!}
            launchLng={ev.launch_longitude!}
            audienceLat={ev.audience_latitude}
            audienceLng={ev.audience_longitude}
            siteMapShare={siteMapShare}
          />
        ) : (
          <EmptyState variant="area" className="h-full justify-center !py-0">
            {noLocationAcked
              ? 'No launch location. Acknowledged.'
              : 'Event has no launch coordinates. Set them on the event detail page first.'}
          </EmptyState>
        )}
      </div>

      {/* Below-map grid: workflow / nearby / messages, each in its own
          column. Workflow keeps ~50%, nearby and messages each ~25% — the
          two right-side columns are equal-width so the messages thread reads
          as a peer of the historical-context panel. */}
      <div className="grid grid-cols-12 flex-1 min-h-0">
        <section className="col-span-12 lg:col-span-6 lg:border-r border-gray-700/40">
        {/* FAA data */}
        <div className="px-5 py-4 border-b border-gray-700/40">
          <SectionHeader label={`FAA Data ${lookupLoading ? '· loading…' : ''}`} color="bg-sky-500/60" />
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-on-surface-variant">UASFM Ceiling</div>
              <div className="text-on-surface font-mono mt-0.5">
                {uasfmError
                  ? <span className="text-amber-300/90">Unavailable</span>
                  : uasfm
                    ? (uasfm.max_ceiling_ft != null
                        ? (uasfm.max_ceiling_ft === 0
                            ? <span className="text-red-300">0 ft · no-fly</span>
                            : `${uasfm.max_ceiling_ft} ft`)
                        : <span className="text-on-surface-variant">No cell · likely Class G</span>)
                    : '—'}
              </div>
              {uasfm?.facility_name && (
                <div className="text-[11px] text-on-surface-variant mt-0.5">
                  {uasfm.facility_name}
                  {uasfm.controlling_facility ? ` (${uasfm.controlling_facility})` : ''}
                </div>
              )}
              {uasfmError && (
                <div className="text-[10px] text-amber-300/70 mt-0.5 break-words">{uasfmError}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-on-surface-variant">Airspace Class</div>
              <div className="text-on-surface mt-0.5">
                {uasfmError
                  ? <span className="text-amber-300/90">—</span>
                  : uasfm?.airspace_class
                    ? <span className="font-mono">Class {uasfm.airspace_class}</span>
                    : <span className="text-on-surface-variant">G / unknown</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Verify coords */}
        <div className="px-5 py-4 border-b border-gray-700/40">
          <SectionHeader label="Step 1 · Safety Map" color="bg-emerald-500/60" />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-on-surface flex items-center gap-2 flex-wrap">
              {coordsVerified ? (
                <>
                  <span className="text-emerald-400">Verified ✓</span>
                  <SignOff
                    firstname={ev.verified_by_firstname}
                    lastname={ev.verified_by_lastname}
                    at={ev.airspace_coords_verified_at}
                    verb="Verified"
                  />
                </>
              ) : noLocationAcked ? (
                <>
                  <span>No location acknowledged</span>
                  <SignOff
                    firstname={ev.no_location_ack_firstname}
                    lastname={ev.no_location_ack_lastname}
                    at={ev.airspace_no_location_ack_at}
                    verb="Acknowledged"
                  />
                </>
              ) : hasCoords ? (
                <span>Confirm the launch + audience coordinates above match the event.</span>
              ) : (
                <span>This event has no launch location.</span>
              )}
            </div>
            {!coordsVerified && hasCoords && (
              <button
                onClick={handleVerifyCoords}
                className={buttonClass('filled')}
              >
                Verify Coordinates
              </button>
            )}
            {canAcknowledgeNoLocation && (
              <button
                type="button"
                onClick={handleAcknowledgeNoLocation}
                disabled={acking}
                className={buttonClass('quiet')}
              >
                {acking ? 'Acknowledging…' : 'Acknowledge no location'}
              </button>
            )}
            {!hasCoords && noLocationAcked && (
              <button
                type="button"
                onClick={handleUndoNoLocation}
                disabled={acking}
                className={buttonClass('quiet')}
              >
                {acking ? 'Putting back…' : 'Put back in the queue'}
              </button>
            )}
          </div>
          {canAcknowledgeNoLocation && (
            <p className="mt-2 text-xs text-on-surface-variant">
              Marks this event complete and takes it out of the queue. Adding a launch location brings it back.
            </p>
          )}
        </div>

        {/* Step 2: Decide */}
        <Reveal show={!!coordsVerified}>
          <div className="px-5 py-4 border-b border-gray-700/40">
            <SectionHeader label="Step 2 · Decision" color="bg-amber-500/60" />
            <FadeIn key={decisionMade ? 'decided' : 'undecided'}>
            {!decisionMade ? (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-on-surface">Is airspace authorization needed?</span>
                <button
                  onClick={() => handleDecide(true)}
                  className={buttonClass('warn', 'sm')}
                >
                  Yes — needs authorization
                </button>
                <button
                  onClick={() => handleDecide(false)}
                  className={buttonClass('good', 'sm')}
                >
                  No — auth not needed
                </button>
              </div>
            ) : (
              <div className="text-sm text-on-surface flex items-center gap-2 flex-wrap">
                <span>Decided: <strong className="text-on-surface">{ev.airspace_needed ? 'Authorization needed' : 'No authorization needed'}</strong></span>
                <SignOff
                  firstname={ev.decided_by_firstname}
                  lastname={ev.decided_by_lastname}
                  at={ev.airspace_decided_at}
                  verb="Decided"
                />
                <button
                  onClick={() => handleDecide(!ev.airspace_needed)}
                  className={`${buttonClass('quiet', 'sm')} ml-auto`}
                >
                  change decision
                </button>
              </div>
            )}
            </FadeIn>
          </div>
        </Reveal>

        {/* Step 3: References */}
        <Reveal show={!!(decisionMade && ev.airspace_needed)}>
          <div className="px-5 py-4 border-b border-gray-700/40">
            <SectionHeader
              label={`Step 3 · References (${data.activeRefs.length})`}
              color="bg-purple-500/60"
            />
            <div className="space-y-2">
              {data.activeRefs.map((r: any) => (
                <AirspaceReferenceRow key={r.id} reference={r} onChange={refreshAfterMutation} />
              ))}
              {data.activeRefs.length === 0 && (
                <EmptyState variant="line" dense>No references yet. Add one to track an FAA application.</EmptyState>
              )}
            </div>
            {/* Add-reference picker — placed below the list so it reads as
                "append a new row" rather than "act on the existing rows". */}
            <div className="mt-3 flex items-center gap-2">
              <Select
                label="Add a reference"
                size="sm"
                width="fit"
                value={null}
                placeholder="+ Add a reference…"
                disabled={adding}
                onChange={(k) => handleAddReference(k as ReferenceType)}
                options={REF_TYPES.map(t => ({ key: t, label: REFERENCE_TYPE_LABELS[t] }))}
              />
              {adding && <span className="text-[11px] text-on-surface-variant italic">adding…</span>}
            </div>
          </div>
        </Reveal>


        {/* Archived attempts. Eligibility (`can_reaffirm`) is computed
            server-side on each row — see AirspaceService.getEventAirspace. When
            any reaffirm-eligible row exists we expand the section by default
            so the pilot doesn't have to dig. */}
        {data.archivedRefs.length > 0 && (() => {
          const eligibleCount = data.archivedRefs.filter((r: any) => r.can_reaffirm).length;
          const anyReaffirmInFlight = reaffirmingId !== null;
          return (
            <details className="px-5 py-4 border-b border-gray-700/40" open={eligibleCount > 0}>
              <summary className="cursor-pointer text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold hover:text-on-surface">
                Previous attempt(s) — {data.archivedRefs.length} archived
                {eligibleCount > 0 && (
                  <span className="ml-2 normal-case tracking-normal text-amber-400/90">
                    · {eligibleCount} can be reaffirmed if still valid
                  </span>
                )}
              </summary>
              <ul className="mt-3 space-y-2 text-xs">
                {data.archivedRefs.map((r: any) => {
                  const driveUrl = r.drive_file_id
                    ? `https://drive.google.com/file/d/${r.drive_file_id}/view`
                    : null;
                  return (
                    <li key={r.id} className="flex items-center gap-2 flex-wrap text-on-surface-variant">
                      <span className="font-mono text-on-surface-variant">{r.reference_number || '—'}</span>
                      <span className="text-on-surface-disabled">·</span>
                      <span>{REFERENCE_TYPE_LABELS[r.type as ReferenceType] || r.type}</span>
                      <span className="text-on-surface-disabled">·</span>
                      <span className="italic">was {r.status}</span>
                      <span className="text-on-surface-disabled">·</span>
                      <span className="text-on-surface-variant">{r.archived_reason}</span>
                      {driveUrl && (
                        <Tooltip label={r.drive_file_name || 'Open approval PDF'}>
                          <a
                            href={driveUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary/80 hover:text-primary underline"
                          >
                            view file ↗
                          </a>
                        </Tooltip>
                      )}
                      {r.can_reaffirm && (
                        <Tooltip label="Mark this existing authorization as still valid, no re-upload needed">
                          <button
                            type="button"
                            onClick={() => handleReaffirm(r.id)}
                            disabled={anyReaffirmInFlight}
                            className={`${buttonClass('good', 'sm')} ml-auto`}
                          >
                            {reaffirmingId === r.id ? 'Reaffirming…' : 'Still valid — reaffirm'}
                          </button>
                        </Tooltip>
                      )}
                    </li>
                  );
                })}
              </ul>
            </details>
          );
        })()}

        </section>

        {/* Middle column — reference links + nearby history, stacked. */}
        <aside className="col-span-12 lg:col-span-3 px-4 py-4 border-t lg:border-t-0 lg:border-r border-gray-700/40 space-y-5">
          {hasCoords && (
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-1 h-3.5 rounded-full bg-blue-500/60" />
                <h3 className="text-[11px] text-on-surface-variant uppercase tracking-widest font-semibold">Reference Links</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ExternalButton href={skyvectorUrl(ev.launch_latitude!, ev.launch_longitude!)} label="SkyVector" hint="VFR sectional" />
                <ExternalButton href={faaNotamSearchUrl(ev.launch_latitude!, ev.launch_longitude!)} label="FAA NOTAM Search" hint="active TFRs" />
                <ExternalButton href={FAA_TFR_URL} label="FAA TFRs" hint="all current TFRs" />
                <ExternalButton href={aloftLaancUrl(ev.launch_latitude!, ev.launch_longitude!)} label="Aloft LAANC" hint="submit auth" emphasis />
                <ExternalButton href={FAA_DRONEZONE_URL} label="FAA DroneZone" hint="Part 107 waivers" emphasis />
                <ExternalButton href={FAA_PART107_WAIVER_URL} label="Part 107 Waiver" hint="altitude / night" />
                {ev.airspace_drive_folder_id && (
                  <ExternalButton href={`https://drive.google.com/drive/folders/${ev.airspace_drive_folder_id}`} label="Drive folder" hint="approval files" />
                )}
              </div>
            </div>
          )}
          {hasCoords && (
            <NearbyHistoricalEvents
              lat={ev.launch_latitude!}
              lng={ev.launch_longitude!}
              facility={ev.controlling_facility || uasfm?.controlling_facility}
              excludeEventId={ev.id}
              onSelect={(id) => {
                // Stay in-SPA: defer to parent state update if available;
                // fall back to a hard nav only if onSelectEvent isn't wired.
                if (onSelectEvent) onSelectEvent(id);
                else window.location.assign(`/pilots/airspace?event=${id}`);
              }}
            />
          )}
        </aside>

        {/* Right column — the universal thread, entity_type 'event_airspace'.
            A show's airspace conversation is SEPARATE from its general one: both
            are keyed on the same event id and kept apart by entity_type, and this
            one is gated to pilot|operations|producer. History moved across in
            migration 026. MessageThread manages its own height and its own
            small-screen presentation, so the old bare/title props are gone. */}
        <aside className="col-span-12 lg:col-span-3 px-4 py-4 border-t lg:border-t-0 border-gray-700/40 flex flex-col overflow-hidden min-h-0">
          <MessageThread entityType="event_airspace" entityId={ev.id} />
        </aside>
      </div>
    </div>
  );
};

export default AirspaceEventDetail;
